// D-190 reference prices in other currencies for /pricing. Checkout stays in USD; FX_RATES are a
// dated snapshot for reference only (refresh the rates and FX_DATE together).
import type { Locale } from "../../src/i18n/locales";

export const FX_DATE = "2026-10-26";
export const FX_RATES: Record<string, number> = {
  USD: 1, TWD: 32.6, HKD: 7.84, SGD: 1.28, MYR: 4.09, CNY: 6.68, JPY: 157, KRW: 1324, PHP: 63.7, THB: 33.8,
  VND: 25830, IDR: 18090, EUR: 0.892, GBP: 0.748, AUD: 1.42, CAD: 1.4, MXN: 18.7, BRL: 5, TRY: 49.8,
};
// Where Intl writes a bare "$" (zh-TW for TWD, es for MXN…), spell the dollar out.
const DOLLAR_PREFIX: Record<string, string> = { TWD: "NT$", HKD: "HK$", SGD: "S$", AUD: "A$", CAD: "C$", MXN: "MX$" };
const TIMEZONE_CURRENCY: Array<[RegExp, string]> = [
  [/^Asia\/Taipei$/, "TWD"], [/^Asia\/(Hong_Kong|Macau)$/, "HKD"], [/^Asia\/Singapore$/, "SGD"], [/^Asia\/(Kuala_Lumpur|Kuching)$/, "MYR"],
  [/^Asia\/(Shanghai|Chongqing|Urumqi|Harbin)$/, "CNY"], [/^Asia\/Tokyo$/, "JPY"], [/^Asia\/Seoul$/, "KRW"], [/^Asia\/Manila$/, "PHP"],
  [/^Asia\/Bangkok$/, "THB"], [/^Asia\/(Ho_Chi_Minh|Saigon)$/, "VND"], [/^Asia\/(Jakarta|Makassar|Jayapura|Pontianak)$/, "IDR"],
  [/^Europe\/(London|Belfast)$/, "GBP"], [/^Europe\/Istanbul$/, "TRY"], [/^Australia\//, "AUD"],
  [/^America\/(Toronto|Vancouver|Montreal|Edmonton|Winnipeg|Halifax|Regina|St_Johns|Moncton)$/, "CAD"],
  [/^America\/(Mexico_City|Cancun|Monterrey|Tijuana|Merida|Chihuahua|Hermosillo|Mazatlan|Matamoros|Bahia_Banderas)$/, "MXN"],
  [/^America\/(Sao_Paulo|Bahia|Fortaleza|Recife|Manaus|Belem|Maceio|Cuiaba|Campo_Grande|Porto_Velho|Araguaina|Boa_Vista|Rio_Branco|Santarem|Noronha)$/, "BRL"],
  [/^Europe\/(Paris|Berlin|Madrid|Rome|Brussels|Amsterdam|Vienna|Dublin|Lisbon|Helsinki|Athens|Luxembourg|Monaco|Bratislava|Ljubljana|Tallinn|Riga|Vilnius|Malta|Zagreb|Andorra)$/, "EUR"],
  [/^Atlantic\/(Canary|Madeira|Azores)$/, "EUR"],
];
export const LOCALE_CURRENCY: Partial<Record<Locale, string>> = { "zh-Hant": "TWD", "zh-Hans": "CNY", fr: "EUR", "pt-BR": "BRL", tr: "TRY", vi: "VND", id: "IDR" };

export const currencyForTimezone = (zone: string): string | null => TIMEZONE_CURRENCY.find(([pattern]) => pattern.test(zone))?.[1] ?? null;

/** Reference amount in another currency: whole units from 50 up, three significant digits from 1,000 up. */
export function formatLocalPrice(usd: number, currency: string, intl: string): string {
  const raw = usd * (FX_RATES[currency] ?? 1);
  const value = raw >= 1000 ? Number(raw.toPrecision(3)) : raw >= 50 ? Math.round(raw) : raw;
  const whole = raw >= 50 || ["JPY", "KRW", "VND", "IDR"].includes(currency);
  const options: Intl.NumberFormatOptions = { style: "currency", currency, ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}) };
  const text = new Intl.NumberFormat(intl, options).format(value);
  const prefix = DOLLAR_PREFIX[currency];
  if (prefix && !/[A-Z]\$|\$[A-Z]|[A-Z]{3}/.test(text)) {
    return prefix + new Intl.NumberFormat(intl, whole ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }
  return text;
}
